/**
 * File store — leaf module (Perf 3.1.b Step 4). Uploaded-file lifecycle.
 * Sibling stores via getStores().
 */
import { create } from 'zustand';
import { formatFileSize } from '@/constants/utils/files';
import { getTranslation } from '@/hooks/useTranslation';
import { filesApi } from '@/lib/api/files';
import { createFileProcessingPoller } from '@/lib/pollFileProcessing';

const getStores = () => require('@/store');

const pollFileProcessingStatus = createFileProcessingPoller({
  fetchFile: (id, options) => filesApi.fetchFile(id, options),
  getFile: id => useFileStore.getState().files[id],
  updateFile: (id, file) => {
    useFileStore.getState().actions.addFiles(file);
    getStores().useChatInputStore.getState().updateFileById(id, {
      processingStatus: file.processingStatus,
      ...(file.metadata ? { metadata: file.metadata } : {}),
    });
  },
});

const useFileStore = create((set, get) => ({
  // State
  files: {}, // Map of all files by id
  myFileIds: [], // Array of file ids for my files listing
  searchFileIds: [], // Array of file ids from search results
  generatedImageIds: [], // Array of file ids for AI-generated images
  generatedVideoIds: [], // Array of file ids for AI-generated videos
  generatedMusicIds: [], // Array of file ids for AI-generated music
  isLoading: false,
  uploadLoading: false,
  libraryRevision: 0,
  allowedFileTypes: null, // New state for allowed file types
  errors: {
    fetch: null,
    upload: null,
    update: null,
    delete: null,
    search: null,
    allowedTypes: null, // New error state
    generatedImages: null, // Error state for generated images
    generatedVideos: null, // Error state for generated videos
    generatedMusic: null, // Error state for generated music
  },
  uploadStats: {
    dailyTotal: 0,
    remaining: 0,
    limit: 0
  },
  pagination: {
    myFiles: {
      page: 1,
      pages: 1,
      total: 0,
      perPage: 20,
      hasMore: false
    },
    search: {
      page: 1,
      pages: 1,
      total: 0,
      perPage: 20,
      hasMore: false
    },
    generatedImages: {
      page: 1,
      pages: 1,
      total: 0,
      perPage: 20,
      hasMore: false
    },
    generatedVideos: {
      page: 1,
      pages: 1,
      total: 0,
      perPage: 20,
      hasMore: false
    },
    generatedMusic: {
      page: 1,
      pages: 1,
      total: 0,
      perPage: 20,
      hasMore: false
    }
  },
  search: {
    query: '',
    fileType: null
  },

  actions: {
    addFiles: (newFiles = []) => {
      if (!Array.isArray(newFiles)) {
        newFiles = [newFiles];
      }

      // Deduplicate files by ID using a Map
      const uniqueFilesMap = new Map();
      newFiles.forEach(file => {
        const fileId = file?.id || file?._id;
        if (file && fileId) {
          uniqueFilesMap.set(String(fileId), { ...file, id: String(fileId) });
        }
      });
      newFiles = Array.from(uniqueFilesMap.values());
      
      const processedFiles = newFiles.map(file => {
        return {
          id: String(file.id || file._id),
          owner: file.owner,
          name: file.name,
          originalName: file.originalName,
          mimeType: file.mimeType,
          sizeInBytes: file.sizeInBytes,
          extension: file.extension,
          fileType: file.fileType,
          type: file.type, // compatible with old code
          fingerprint: file.fingerprint,
          blurhash: file.blurhash,
          source: file.source,
          processingStatus: file.processingStatus,
          metadata: file.metadata,
          parameters: file.parameters,
          thumbnailPath: file.thumbnailPath,
          cdnPath: file.cdnPath,
          status: file.status,
          isExpired: file.isExpired,
          expiresAt: file.expiresAt,
          createdAt: file.createdAt,
          updatedAt: file.updatedAt,
          deletedAt: file.deletedAt,
          // Preserve conversation/message linkage for generated media
          conversationId: file.conversationId,
          messageId: file.messageId,
        };
      });
      
      set(state => ({
        files: {
          ...state.files,
          ...Object.fromEntries(processedFiles.map(file => [file.id, file]))
        }
      }));

      return processedFiles;
    },

    searchFiles: async (query, page = 1, fileType = null, source = 'user') => {
      if (!getStores().useAuthStore.getState().token) {
        set(state => ({
          isLoading: false,
          errors: { ...state.errors, search: null },
        }));
        return [];
      }
      try {
        set(state => ({ 
          isLoading: true, 
          errors: { ...state.errors, search: null },
          search: { query, fileType, source }
        }));
        
        const { files, pagination } = await filesApi.search(query, page, fileType, source);
        
        get().actions.addFiles(files);
        
        set(state => {
          const fileIds = files.map(f => f.id);
          const total = typeof pagination?.total === 'number'
            ? pagination.total
            : parseInt(pagination?.total, 10) || 0;
          const perPage = pagination?.perPage || state.pagination.search.perPage || 20;
          const pg = pagination?.page || page;

          return {
            searchFileIds: page === 1 ? fileIds : [...state.searchFileIds, ...fileIds],
            pagination: {
              ...state.pagination,
              search: {
                page: pg,
                pages: pagination.pages ?? (Math.ceil(total / perPage) || 1),
                total,
                perPage,
                hasMore: typeof pagination.hasMore === 'boolean'
                  ? pagination.hasMore
                  : (total > pg * perPage),
              }
            },
            isLoading: false 
          };
        });

        return files;
      } catch (error) {
        set(state => ({
          errors: { ...state.errors, search: error.message || 'Failed to search files' },
          isLoading: false 
        }));
        throw error;
      }
    },

    uploadFile: async ({ file, name, collectionId, libraryUpload = false }) => {
      try {
        set(state => ({ 
          uploadLoading: true, 
          errors: { ...state.errors, upload: null }
        }));

        // Auto-detect if the active conversation is temporary so the backend
        // can tag the file with flags.isFromTemporaryChat and link it via conversationId
        // for cascade deletion when the temporary conversation is deleted.
        const contextId = getStores().useChatInputStore.getState().currentContextId;
        const currentChat = !libraryUpload && contextId
          ? getStores().useChatStore.getState().chats.find(c => c.id === contextId)
          : null;
        const isFromTemporaryChat = currentChat?.flags?.isTemporary === true;
        const conversationId = isFromTemporaryChat ? contextId : undefined;
        // Prefer an explicit collectionId (e.g. collection page composer).
        // Fall back to the active chat's collection so uploads inside a
        // collection chat land in Collection.files, not only the global library.
        const resolvedCollectionId = collectionId || currentChat?.inCollectionId || undefined;

        const response = await filesApi.uploadFile({
          file,
          name,
          collectionId: resolvedCollectionId,
          conversationId,
          isFromTemporaryChat,
        });

        let targetFile = response.file || response.existingFile;
        
        // Process file through addFiles helper
        get().actions.addFiles(response.file);
        
        // Add new file to state - now handling files as an object
        set(state => ({
          files: {
            ...state.files,
            [targetFile.id]: targetFile
          },
          myFileIds: [targetFile.id, ...state.myFileIds.filter(id => id !== targetFile.id)],
          libraryRevision: (state.libraryRevision || 0) + 1,
          uploadStats: response.uploadStats,
          uploadLoading: false,
          pagination: {
            ...state.pagination,
            myFiles: {
              ...state.pagination.myFiles,
              total: (state.pagination.myFiles.total || 0) + (state.myFileIds.includes(targetFile.id) ? 0 : 1),
            },
          },
        }));

        // Keep collection Files tabs in sync without a refetch. Backend
        // already $addToSet'd the file when collectionId was sent.
        if (resolvedCollectionId && targetFile?.id) {
          const collection = getStores().useCollectionStore.getState().collections[resolvedCollectionId];
          if (collection) {
            const existingFiles = Array.isArray(collection.files) ? collection.files : [];
            const alreadyHas = existingFiles.some((f) => (f?.id || f) === targetFile.id);
            if (!alreadyHas) {
              getStores().useCollectionStore.getState().addCollection({
                ...collection,
                files: [...existingFiles, targetFile],
              });
            }
          }
        }

        return targetFile;
      } catch (error) {
        set(state => ({ 
          errors: { ...state.errors, upload: error.message || 'Failed to upload file' },
          uploadLoading: false 
        }));
        throw error;
      }
    },

    fetchFiles: async (page = 1, fileType = null, source = 'user') => {
      if (!getStores().useAuthStore.getState().token) {
        set(state => ({
          isLoading: false,
          errors: { ...state.errors, fetch: null },
        }));
        return [];
      }
      try {
        set(state => ({ 
          isLoading: true, 
          errors: { ...state.errors, fetch: null }
        }));
        
        const { files, pagination } = await filesApi.fetchFiles(page, fileType, source);
        
        get().actions.addFiles(files);
        
        set(state => {
          const fileIds = files.map(f => f.id);
          const total = typeof pagination?.total === 'number'
            ? pagination.total
            : parseInt(pagination?.total, 10) || 0;
          const perPage = pagination?.perPage || state.pagination.myFiles.perPage || 20;
          const pg = pagination?.page || page;
          
          return {
            myFileIds: page === 1 ? fileIds : [...state.myFileIds, ...fileIds],
            pagination: {
              ...state.pagination,
              myFiles: {
                page: pg,
                pages: pagination.pages ?? (Math.ceil(total / perPage) || 1),
                total,
                perPage,
                hasMore: typeof pagination.hasMore === 'boolean'
                  ? pagination.hasMore
                  : (total > pg * perPage),
              }
            },
            isLoading: false 
          };
        });

        return files;
      } catch (error) {
        set(state => ({ 
          errors: { ...state.errors, fetch: error.message || 'Failed to fetch files' },
          isLoading: false 
        }));
        throw error;
      }
    },

    /** Lightweight total from GET /json/files/count — same filters as fetchFiles. */
    fetchMyFilesCount: async (fileType = null, source = 'user') => {
      if (!getStores().useAuthStore.getState().token) {
        return null;
      }
      try {
        const { total } = await filesApi.fetchFilesCount(fileType, source);
        const n = typeof total === 'number' ? total : parseInt(total, 10) || 0;
        set(state => {
          const perPage = state.pagination.myFiles.perPage || 20;
          return {
            pagination: {
              ...state.pagination,
              myFiles: {
                ...state.pagination.myFiles,
                total: n,
                pages: Math.max(1, Math.ceil(n / perPage)),
              },
            },
          };
        });
        return n;
      } catch (e) {
        console.error('[fetchMyFilesCount]', e);
        return null;
      }
    },

    // Fetch user images (both uploaded and generated)
    fetchGeneratedImages: async (page = 1) => {
      if (!getStores().useAuthStore.getState().token) {
        set(state => ({
          isLoading: false,
          errors: { ...state.errors, generatedImages: null },
        }));
        return [];
      }
      try {
        set(state => ({ 
          isLoading: true, 
          errors: { ...state.errors, generatedImages: null }
        }));
        
        const { files, pagination } = await filesApi.fetchFiles(page, 'image', 'inference');
        
        get().actions.addFiles(files);
        
        set(state => {
          const fileIds = files.map(f => f.id);
          
          return {
            generatedImageIds: page === 1 ? fileIds : [...state.generatedImageIds, ...fileIds],
            pagination: {
              ...state.pagination,
              generatedImages: {
                page: pagination.page,
                pages: pagination.pages,
                total: pagination.total,
                perPage: pagination.perPage,
                hasMore: pagination.hasMore
              }
            },
            isLoading: false 
          };
        });

        return files;
      } catch (error) {
        set(state => ({ 
          errors: { ...state.errors, generatedImages: error.message || 'Failed to fetch generated images' },
          isLoading: false 
        }));
        throw error;
      }
    },

    // Fetch user generated videos
    fetchGeneratedVideos: async (page = 1) => {
      if (!getStores().useAuthStore.getState().token) {
        set(state => ({
          isLoading: false,
          errors: { ...state.errors, generatedVideos: null },
        }));
        return [];
      }
      try {
        set(state => ({ 
          isLoading: true, 
          errors: { ...state.errors, generatedVideos: null }
        }));
        
        const { files, pagination } = await filesApi.fetchFiles(page, 'video', 'inference');

        get().actions.addFiles(files);
        
        set(state => {
          const fileIds = files.map(f => f.id);
          
          return {
            generatedVideoIds: page === 1 ? fileIds : [...state.generatedVideoIds, ...fileIds],
            pagination: {
              ...state.pagination,
              generatedVideos: {
                page: pagination.page,
                pages: pagination.pages,
                total: pagination.total,
                perPage: pagination.perPage,
                hasMore: pagination.hasMore
              }
            },
            isLoading: false 
          };
        });

        return files;
      } catch (error) {
        set(state => ({ 
          errors: { ...state.errors, generatedVideos: error.message || 'Failed to fetch generated videos' },
          isLoading: false 
        }));
        throw error;
      }
    },

    // Fetch user generated music/audio
    fetchGeneratedMusic: async (page = 1) => {
      if (!getStores().useAuthStore.getState().token) {
        set(state => ({
          isLoading: false,
          errors: { ...state.errors, generatedMusic: null },
        }));
        return [];
      }
      try {
        set(state => ({ 
          isLoading: true, 
          errors: { ...state.errors, generatedMusic: null }
        }));
        
        const { files, pagination } = await filesApi.fetchFiles(page, 'audio', 'inference');
        
        get().actions.addFiles(files);
        
        set(state => {
          const fileIds = files.map(f => f.id);
          
          return {
            generatedMusicIds: page === 1 ? fileIds : [...state.generatedMusicIds, ...fileIds],
            pagination: {
              ...state.pagination,
              generatedMusic: {
                page: pagination.page,
                pages: pagination.pages,
                total: pagination.total,
                perPage: pagination.perPage,
                hasMore: pagination.hasMore
              }
            },
            isLoading: false 
          };
        });

        return files;
      } catch (error) {
        set(state => ({ 
          errors: { ...state.errors, generatedMusic: error.message || 'Failed to fetch generated music' },
          isLoading: false 
        }));
        throw error;
      }
    },

    // Update a file in the store (local state update, not API call)
    updateFileInStore: (fileId, updates) => {
      set(state => {
        const existingFile = state.files[fileId];
        if (!existingFile) return state;
        
        return {
          files: {
            ...state.files,
            [fileId]: {
              ...existingFile,
              ...updates
            }
          }
        };
      });
    },

    // Fetch a single file by ID and update the store
    fetchFileById: async (fileId) => {
      try {
        if (!getStores().useAuthStore.getState().token) {
          return null;
        }
        if (!filesApi.fetchFile) {
          console.warn('[fetchFileById] filesApi.fetchFile not available');
          return null;
        }
        const file = await filesApi.fetchFile(fileId);
        get().actions.addFiles(file);
        return file;
      } catch (error) {
        console.error('Error fetching file by ID:', error);
        throw error;
      }
    },

    pollFileProcessingStatus,

    updateFile: async (fileId, name) => {
      try {
        set(state => ({
          errors: { ...state.errors, update: null }
        }));

        const updatedFile = await filesApi.updateFile(fileId, name);
        
        // Process updated file through addFiles helper
        get().actions.addFiles(updatedFile);
        set(state => ({ libraryRevision: (state.libraryRevision || 0) + 1 }));

        return updatedFile;
      } catch (error) {
        set(state => ({
          errors: { ...state.errors, update: error.message || 'Failed to update file' }
        }));
        throw error;
      }
    },

    deleteFile: async (fileId) => {
      try {
        set(state => ({
          errors: { ...state.errors, delete: null }
        }));

        await filesApi.deleteFile(fileId);
        
        // Remove file references from state
        set(state => ({
          libraryRevision: (state.libraryRevision || 0) + 1,
          myFileIds: state.myFileIds.filter(id => id !== fileId),
          searchFileIds: state.searchFileIds.filter(id => id !== fileId),
          generatedImageIds: state.generatedImageIds.filter(id => id !== fileId),
          generatedVideoIds: state.generatedVideoIds.filter(id => id !== fileId),
          generatedMusicIds: state.generatedMusicIds.filter(id => id !== fileId),
          pagination: {
            ...state.pagination,
            myFiles: {
              ...state.pagination.myFiles,
              total: Math.max(0, (state.pagination.myFiles.total || 0) - 1),
            },
            search: state.searchFileIds.includes(fileId)
              ? {
                  ...state.pagination.search,
                  total: Math.max(0, (state.pagination.search.total || 0) - 1),
                }
              : state.pagination.search,
          },
          // Note: We keep the file in the files map in case it's referenced elsewhere
        }));
      } catch (error) {
        set(state => ({
          errors: { ...state.errors, delete: error.message || 'Failed to delete file' }
        }));
        throw error;
      }
    },

    deleteAllFiles: async (password, scope = 'all') => {
      if (scope !== 'all' && scope !== 'generated') throw new Error('Invalid file deletion scope');
      const userId = getStores().useAuthStore.getState().user?.id;
      if (!userId) throw new Error('Authentication required');
      const sameAccount = () => getStores().useAuthStore.getState().user?.id === userId;
      let before;
      try {
        set(state => ({ errors: { ...state.errors, delete: null } }));
        let hasMore;
        do {
          if (!sameAccount()) throw new Error('Account changed. File deletion stopped.');
          // Authentication can yield before selecting a credential. Keep the
          // confirmed owner authoritative through hydration and token refresh.
          const result = await filesApi.deleteAllFilesBatch(password, before, scope, { isRequestCurrent: sameAccount });
          if (!sameAccount()) throw new Error('Account changed. File deletion stopped.');
          before = result.before;
          hasMore = result.hasMore;
          const deleted = new Set(result.deletedFileIds);
          set(state => {
            const files = { ...state.files };
            for (const id of deleted) delete files[id];
            const lists = {
              myFiles: 'myFileIds', search: 'searchFileIds', generatedImages: 'generatedImageIds',
              generatedVideos: 'generatedVideoIds', generatedMusic: 'generatedMusicIds',
            };
            const updates = { files, pagination: { ...state.pagination } };
            for (const [page, key] of Object.entries(lists)) {
              updates[key] = state[key].filter(id => !deleted.has(id));
              const clearedScope = scope === 'all' || page.startsWith('generated');
              updates.pagination[page] = clearedScope && !hasMore && !result.failedCount
                ? { ...state.pagination[page], page: 1, pages: 1, total: updates[key].length, hasMore: false }
                : { ...state.pagination[page],
                    total: Math.max(0, state.pagination[page].total - (state[key].length - updates[key].length)) };
            }
            return updates;
          });
          const collections = getStores().useCollectionStore.getState();
          for (const collection of Object.values(collections.collections)) {
            const attachments = collection.files || [];
            const remaining = attachments.filter(file => !deleted.has(String(file?.id || file?._id || file)));
            if (remaining.length !== attachments.length) collections.addCollection({ ...collection, files: remaining });
          }
          for (const id of deleted) getStores().useChatInputStore.getState().removeFileByIdFromAllContexts(id);
          if (result.failedCount) throw new Error(getTranslation('settings.general.dangerZone.deleteFiles.partialError'));
          if (hasMore && !deleted.size) throw new Error(getTranslation('settings.general.errors.operationFailed'));
        } while (hasMore);
      } catch (error) {
        if (sameAccount()) set(state => ({ errors: { ...state.errors, delete: error.message || 'Failed to delete files' } }));
        throw error;
      } finally {
        // Refresh even after a timeout: the server may have completed part of a batch.
        if (sameAccount()) set(state => ({ libraryRevision: (state.libraryRevision || 0) + 1 }));
      }
    },

    // Helper functions
    getFileById: (fileId) => {
      return get().files[fileId];
    },

    getMyFiles: () => {
      const state = get();
      return state.myFileIds.map(id => state.files[id]);
    },

    getSearchFiles: () => {
      const state = get();
      return state.searchFileIds.map(id => state.files[id]);
    },

    ensureAllowedFileTypes: async () => {
      const state = get();
      if (state.allowedFileTypes && Object.keys(state.allowedFileTypes).length > 0) {
        return state.allowedFileTypes;
      }
      return await get().actions.fetchAllowedFileTypes(true);
    },

    isFileAllowed: (file) => {
      try {
        const state = get();
        const allowedTypes = state.allowedFileTypes;

        if (!allowedTypes) {
          // The allowlist fetch failed (offline, transient API error, etc.).
          // Don't hard-reject every file — that produced "unable to verify"
          // toasts for perfectly valid uploads. Defer to the server, which
          // enforces the same allowlist on POST /json/files/new.
          console.warn('[isFileAllowed] allowedFileTypes unavailable - deferring validation to server');
          return { isAllowed: true, message: null };
        }

        // Get file type and extension
        const fileType = file.type || file.mimeType;
        let fileExtension = file.extension || 
          (file.name ? file.name.split('.').pop().toLowerCase() : null) ||
          (file.originalName ? file.originalName.split('.').pop().toLowerCase() : null);

        // First try direct MIME type match
        let allowedConfig = allowedTypes[fileType];

        // If MIME type not found, try to find by extension
        if (!allowedConfig && fileExtension) {
          // Look through all allowed types for matching extension
          for (const [mimeType, config] of Object.entries(allowedTypes)) {
            if (config.extensions.includes(fileExtension)) {
              allowedConfig = config;
              break;
            }
          }
        }

        // If still no match found, file type is not allowed
        if (!allowedConfig) {
          return { isAllowed: false, message: `File type (${fileType}) not allowed` };
        }

        // Check if the file size is within limits
        const fileSize = file.size || file.sizeInBytes;
        if (fileSize > allowedConfig.maxSize) {
          return { isAllowed: false, message: `File size (${formatFileSize(fileSize)}) exceeds the maximum allowed size (${formatFileSize(allowedConfig.maxSize)})` };
        }

        return { isAllowed: true, message: null };
      } catch (error) {
        console.error('Error checking if file is allowed:', error);
        return { isAllowed: false, message: getTranslation('store.uploadError')};
      }
    },

    // Check if user has credits available for file upload
    // A missing/stale balance must not prevent attaching context to Chat.
    // Returns Promise<{ hasCredits: boolean, message: string | null }>.
    hasCreditsForFileUpload: async () => {
      try {
        const auth = getStores().useAuthStore.getState();
        const userCredits = auth.user?.credits?.totalAvailable;
        if (Number.isFinite(userCredits) && userCredits > 0) {
          return { hasCredits: true, message: null };
        }
        const refreshed = await auth.fetchUserCredits?.({ force: true });
        if (auth.user?.id !== getStores().useAuthStore.getState().user?.id) {
          return { hasCredits: false, message: getTranslation('store.uploadError') };
        }
        // Only a fresh, explicit zero can reject the attachment. If refresh
        // fails, the upload/inference services still enforce their own limits.
        const hasCredits = !Number.isFinite(refreshed?.totalAvailable) || refreshed.totalAvailable > 0;
        
        if (!hasCredits) {
          return { 
            hasCredits: false, 
            message: getTranslation('store.needCreditsForUpload') 
          };
        }
        
        return { hasCredits: true, message: null };
      } catch (error) {
        console.error('Error checking credits for file upload:', error);
        // In case of error, allow the upload to proceed and let the API handle it
        return { hasCredits: true, message: null };
      }
    },

    clearSearch: () => {
      set(state => ({
        searchFileIds: [],
        pagination: {
          ...state.pagination,
          search: {
            page: 1,
            pages: 1,
            total: 0,
            perPage: 20,
            hasMore: false
          }
        },
        search: {
          query: '',
          fileType: null
        }
      }));
    },

    fetchAllowedFileTypes: async (force = false) => {
      try {
        // Check if we already have data (skip cache when force=true)
        if (!force) {
          const currentAllowedTypes = get().allowedFileTypes;
          if (currentAllowedTypes && Object.keys(currentAllowedTypes).length > 0) {
            return currentAllowedTypes;
          }
        }

        set(state => ({ 
          errors: { ...state.errors, allowedTypes: null }
        }));
        
        const allowedTypes = await filesApi.getAllowedFileTypes();
        
        set(() => ({
          allowedFileTypes: allowedTypes
        }));

        return allowedTypes;
      } catch (error) {
        set(state => ({ 
          errors: { ...state.errors, allowedTypes: error.message || 'Failed to fetch allowed file types' }
        }));
        throw error;
      }
    },
  },
}));

export { useFileStore };
export default useFileStore;
